import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '@/hooks/useApi';
import type { DashboardStats, NotificationItem } from '@/lib/types';

interface ClearedEntry {
  id: string;
  clearedAt: string;
}

const STORAGE_KEY = 'clearedNotifications';
const SNOOZE_DAYS = 7;

function loadCleared(): ClearedEntry[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
}

// Server computes notification candidates from live data (service pickups,
// deliveries, appointments); snoozing is a client-side concern.
export function useNotifications() {
  const { data, refetch } = useApi<{ notifications: NotificationItem[] }>(
    '/dashboard/stats?notifications=1',
  );
  const [cleared, setCleared] = useState<ClearedEntry[]>(loadCleared);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cleared));
  }, [cleared]);

  const isSnoozed = useCallback((id: string) => {
    const entry = cleared.find((c) => c.id === id);
    if (!entry) return false;
    const days = (Date.now() - new Date(entry.clearedAt).getTime()) / 86_400_000;
    return days < SNOOZE_DAYS;
  }, [cleared]);

  const notifications = useMemo(
    () => (data?.notifications ?? []).filter((n) => !isSnoozed(n.id)),
    [data, isSnoozed],
  );

  const clearNotification = useCallback((id: string) => {
    setCleared((prev) => [...prev.filter((c) => c.id !== id), { id, clearedAt: new Date().toISOString() }]);
  }, []);

  const clearAll = useCallback(() => {
    const now = new Date().toISOString();
    setCleared((prev) => {
      const ids = new Set(prev.map((c) => c.id));
      const fresh = notifications.filter((n) => !ids.has(n.id)).map((n) => ({ id: n.id, clearedAt: now }));
      return [...prev.map((c) => ({ ...c, clearedAt: now })), ...fresh];
    });
  }, [notifications]);

  const hasOverdue = notifications.some((n) => n.isOverdue);

  return { notifications, count: notifications.length, hasOverdue, clearNotification, clearAll, refetch };
}

export type { DashboardStats };
